import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ExceptionApi, ExceptionTypes, ExceptionUtils } from '@frontend/api-schedule-exceptions';
import { useTranslation } from '@frontend/i18n';
import {
  FormRow,
  Modal,
  ModalControlModalProps,
  useForm,
  TextField,
  RadioField,
  DateRangeField,
  TimeRangeField,
  CheckboxField,
  useModalControl,
  ConfirmationModal,
  Text,
  useAlert,
} from '@frontend/design-system';
import { usePastDateBanner } from '../../hooks/use-past-date-banner';
import { shouldInvalidateExceptions } from '../../utils';
import { ResponsiveModalFooter } from '../settings';
import * as styles from './styles';

interface Props extends ModalControlModalProps {
  data?: ExceptionTypes.ExceptionData;
  locationId: string;
  providerId?: string;
  checkConflicts?: (newException: ExceptionTypes.ExceptionForm) => boolean | undefined;
}

export const ScheduleExceptionsModal = ({
  data,
  locationId,
  providerId,
  checkConflicts,
  ...modalControlProps
}: Props) => {
  const { t } = useTranslation('schedule');
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { createDataTransferObject } = ExceptionUtils;
  const [formEvent, setFormEvent] = useState<React.FormEvent<Element>>();
  const [formError, setFormError] = useState<string>('');

  useEffect(() => {
    reset();
    if (data) {
      seedValues(data);
    }
  }, [data]);

  const { modalProps: addModalProps, triggerProps: addTriggerProps } = useModalControl();

  const { formProps, getFieldProps, reset, seedValues, values } = useForm({
    fields: {
      name: { type: 'text', value: data?.name ?? '' },
      isAvailable: { type: 'radio', required: true, value: data?.isAvailable ?? 'false' },
      dateRange: {
        type: 'dateRange',
        required: true,
        value: [data?.startDate ?? '', data?.endDate ?? ''],
      },
      timeRange: {
        type: 'timeRange',
        required: true,
        value: [data?.startTime ?? '', data?.endTime ?? ''],
        interval: 15,
      },
      isAllDay: {
        type: 'checkbox',
        value:
          data?.isAvailable === 'false' && data?.startTime === '12:00am' && data?.endTime === '11:59pm' ? true : false,
      },
    },
    onSubmit: async (values) => {
      // Reset to false if 'isAllDay' was previously selected, but the user has selected 'available' without deselecting 'isAllDay'
      if (values.isAvailable === 'true') {
        values.isAllDay = false;
      }

      const updateData = createDataTransferObject(values as ExceptionTypes.ExceptionForm) as ExceptionTypes.Exception;

      if (data?.id) {
        updateData.id = data?.id;
      }

      try {
        if (providerId) {
          await ExceptionApi.createProviderException(updateData, locationId, providerId || '');
        } else {
          await ExceptionApi.createException(updateData, locationId);
        }

        if (data?.id) {
          alert.success(t('Successfully updated exception'));
        } else {
          alert.success(t('Successfully created exception'));
        }

        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => shouldInvalidateExceptions(queryKey, locationId, providerId),
        });

        reset();
        modalControlProps.onClose();
      } catch (error: any) {
        console.error(error);
        if (error) {
          setFormError('Something went wrong, please check the form is filled in correctly');
        }
      }
    },
  });

  useEffect(() => {
    setFormError('');
    // Removing error text if any dependant field is changed (until we have field validation for date and time ranges)
  }, [data?.id, values?.dateRange, values?.timeRange, values?.isAllDay]);

  const { pastDateBannerComponent } = usePastDateBanner({
    initialDate: data?.startDate,
    currentDate: values.dateRange?.[0],
    locationId: locationId,
  });

  const handleCheckConflicts = (event?: React.FormEvent<Element>) => {
    if (!checkConflicts) {
      handleAddNew(event);
      return;
    }
    if (checkConflicts(values as ExceptionTypes.ExceptionForm)) {
      setFormEvent(event);
      addTriggerProps.onClick();
    } else {
      handleAddNew(event);
    }
  };

  const handleConfirmAddNew = () => {
    addTriggerProps.onClick();
    handleAddNew(formEvent);
  };

  const handleAddNew = (event?: React.FormEvent<Element>) => {
    if (!event) {
      return;
    }
    formProps.onSubmit(event);
  };

  return (
    <>
      <Modal.Responsive {...modalControlProps} maxWidth={500}>
        {<Modal.Header>{data?.id ? t('Edit Exception') : t('Add Exception')}</Modal.Header>}
        <Modal.Body>
          <form>
            <FormRow css={styles.topRow}>
              <TextField {...getFieldProps('name')} label={t('Name of Exception (optional)')} />
            </FormRow>
            <FormRow>
              <RadioField {...getFieldProps('isAvailable')}>
                <RadioField.Radio value='false'>{t('Unavailable')}</RadioField.Radio>
                <RadioField.Radio value='true'>{t('Available')}</RadioField.Radio>
              </RadioField>
            </FormRow>
            <FormRow>
              <DateRangeField {...getFieldProps('dateRange')} label={t('date range')} />
            </FormRow>
            {/* Force the user to be specific with their time choices if they're adding availability */}
            {getFieldProps('isAvailable').value === 'false' && (
              <FormRow>
                <CheckboxField {...getFieldProps('isAllDay')} label={t('All Day')} />
              </FormRow>
            )}
            {(!getFieldProps('isAllDay').value || getFieldProps('isAvailable').value === 'true') && (
              <FormRow>
                <TimeRangeField {...getFieldProps('timeRange')} label={t('time range')} />
              </FormRow>
            )}
            {formError && <Text color='error'>{formError}</Text>}
          </form>
          {pastDateBannerComponent}
        </Modal.Body>
        <ResponsiveModalFooter
          primary={{
            label: t('Save'),
            onClick: handleCheckConflicts,
          }}
          secondary={{
            label: t('Cancel'),
            onClick: modalControlProps.onClose,
          }}
        />
      </Modal.Responsive>
      <ConfirmationModal
        {...addModalProps}
        title={t('Conflicting Appointment Exception')}
        message={t('This provider exception will take precedence without affecting the appointment exception.')}
        confirmLabel={t('Add Anyway')}
        cancelLabel={t('Cancel')}
        onCancel={addModalProps.onClose}
        onConfirm={handleConfirmAddNew}
      />
    </>
  );
};
